import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useQuote } from "@/context/QuoteContext";
import { useLang } from "@/i18n/LanguageContext";
import { TrendingDown, ArrowRight } from "lucide-react";

const DATA = {
  "Single Implant": { usa: 4000, abroad: 800 },
  "All-on-4 (per arch)": { usa: 26000, abroad: 8500 },
  "All-on-6 (per arch)": { usa: 28000, abroad: 9900 },
  "Crowns": { usa: 1300, abroad: 400 },
  "Veneers": { usa: 1800, abroad: 400 },
};

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

export default function SavingsCalculator() {
  const { openQuote } = useQuote();
  const { t } = useLang();
  const [procedure, setProcedure] = useState("Single Implant");
  const [qty, setQty] = useState([1]);

  const procLabels = t("calc.procedures");
  const procLabel = (k) => procLabels[k] || k;

  const { usaTotal, abroadTotal, saved, pct } = useMemo(() => {
    const d = DATA[procedure];
    const n = qty[0];
    const usaTotal = d.usa * n;
    const abroadTotal = d.abroad * n;
    const saved = usaTotal - abroadTotal;
    return { usaTotal, abroadTotal, saved, pct: Math.round((saved / usaTotal) * 100) };
  }, [procedure, qty]);

  return (
    <Card className="p-6 sm:p-8 rounded-2xl border-border shadow-sm" data-testid="savings-calculator">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-accent" /></div>
        <div>
          <h3 className="font-heading text-xl font-bold leading-none">{t("calc.title")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("calc.sub")}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div className="space-y-2">
          <Label>{t("calc.procedureLabel")}</Label>
          <Select value={procedure} onValueChange={setProcedure}>
            <SelectTrigger className="h-12" data-testid="calc-procedure-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(DATA).map((p) => <SelectItem key={p} value={p}>{procLabel(p)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("calc.quantity")} <span className="text-primary font-semibold">{qty[0]}</span></Label>
          <Slider value={qty} onValueChange={setQty} min={1} max={12} step={1} className="mt-4" data-testid="calc-qty-slider" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground -mt-2 mb-4">{t("calc.perArchNote")}</p>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="rounded-xl bg-muted p-4 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("calc.usaCanada")}</p>
          <p className="text-2xl font-heading font-bold text-foreground mt-1 line-through decoration-destructive/60" data-testid="calc-usa-price">{fmt(usaTotal)}</p>
        </div>
        <div className="rounded-xl bg-primary/10 p-4 text-center">
          <p className="text-xs uppercase tracking-wider text-primary">{t("calc.abroad")}</p>
          <p className="text-2xl font-heading font-bold text-primary mt-1" data-testid="calc-abroad-price">{fmt(abroadTotal)}</p>
        </div>
      </div>

      <div className="rounded-xl bg-primary p-5 text-center text-white mb-5">
        <p className="text-sm opacity-90">{t("calc.youSave")}</p>
        <p className="text-4xl font-heading font-extrabold" data-testid="calc-savings">{fmt(saved)}</p>
        <p className="text-sm opacity-90">{t("calc.thatsOff", { pct })}</p>
      </div>

      <Button className="w-full rounded-full h-12" onClick={() => openQuote({ procedure, type: "quote" })} data-testid="calc-get-quote-btn">
        {t("calc.cta", { procedure: procLabel(procedure) })} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </Card>
  );
}
